/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Monsieur Croissant — the panel, and the launcher that opens it.
 *
 * ── WHERE IT LIVES ──────────────────────────────────────────────────────────
 * Docked in the left sidebar on desktop, floating bottom-right on a phone. Two
 * placements because the same one is wrong in both places: a floating button
 * over a desktop layout is a widget bolted onto an app, and a sidebar row on a
 * phone is unreachable while the tab bar owns the thumb zone.
 *
 * The panel itself is a right-hand drawer on desktop — the app stays visible
 * and usable behind it, which matters because half of what the assistant says
 * is "look at Thursday" — and a full-height sheet on a phone, where a drawer
 * would be a squint.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  chefApi, type ChefBlock, type ChefConversation, type ChefMessage,
} from "../../api/chef.ts";
import { ApiError } from "../../api/client.ts";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { Spinner } from "../../components/ui.tsx";
import { ChefBlockView } from "./ChefBlocks.tsx";

const NAME = "Monsieur Croissant";

/**
 * Status lines shown while a turn runs.
 *
 * The turn is 2–8 seconds and the reply arrives whole, so there is nothing
 * honest to stream. Rotating text is not a progress bar and does not pretend to
 * be one — it is there so a silent eight seconds does not read as a hang. The
 * lines are deliberately about cooking rather than about tokens.
 */
const THINKING = [
  "Reading your week…",
  "Checking the pantry…",
  "Looking through your recipes…",
  "Thinking about it…",
  "Nearly there…",
];

export function ChefPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [conv, setConv] = useState<ChefConversation | null>(null);
  const [messages, setMessages] = useState<ChefMessage[]>([]);
  const [starters, setStarters] = useState<Extract<ChefBlock, { type: "chips" }> | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [thinkingIdx, setThinkingIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  // Start a conversation the first time it opens, and keep it for the session.
  useEffect(() => {
    if (!open || conv) return;
    let cancelled = false;
    void (async () => {
      try {
        const c = await chefApi.newConversation();
        if (cancelled) return;
        setConv(c);
        setMessages(c.messages ?? []);
        setStarters(c.starters);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Couldn't start a conversation.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, conv]);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  // Esc closes. Registered on the document rather than the panel so it works
  // even when focus has wandered into a card.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!sending) return;
    setThinkingIdx(0);
    const t = setInterval(() => setThinkingIdx((i) => (i + 1) % THINKING.length), 2200);
    return () => clearInterval(t);
  }, [sending]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  /**
   * Anything the assistant changed is now stale in the app's caches.
   *
   * Blunt on purpose: invalidating the four keys that could possibly be
   * affected costs one refetch of screens the user is probably looking at, and
   * the alternative — working out which key a given proposal touched — is a
   * mapping that goes stale the first time a proposal kind is added.
   */
  const onMutated = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["plan"] });
    void qc.invalidateQueries({ queryKey: ["recipes"] });
    void qc.invalidateQueries({ queryKey: ["shopping"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  }, [qc]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setDraft("");
    setStarters(null);
    setError(null);
    setSending(true);

    // Optimistic: the question appears immediately. A chat that waits two
    // seconds to show what you typed feels broken even when it isn't.
    const optimistic: ChefMessage = {
      id: `pending-${Date.now()}`,
      role: "user", content: trimmed, blocks: [],
      createdAt: new Date().toISOString(), pending: true,
    };
    setMessages((m) => [...m, optimistic]);

    try {
      const r = await chefApi.send(trimmed, conv?.id ?? null);
      setMessages((m) => [
        ...m.filter((x) => x.id !== optimistic.id),
        r.userMessage,
        r.message,
      ]);
      if (!conv) {
        setConv({
          id: r.conversationId, title: trimmed.slice(0, 48),
          createdAt: optimistic.createdAt, messages: [], starters: null,
        });
      }
    } catch (err) {
      // The optimistic message stays, so the user can see what they asked and
      // retry it rather than retyping.
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    } finally {
      setSending(false);
      input.current?.focus();
    }
  }, [conv, sending]);

  const reset = async () => {
    setMessages([]);
    setError(null);
    try {
      const c = await chefApi.newConversation();
      setConv(c);
      setStarters(c.starters);
    } catch {
      setConv(null);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Scrim on mobile only. On desktop the app behind the drawer stays
          usable — the assistant often tells you to go look at something. */}
      <div
        className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm md:hidden"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={NAME}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-rule-soft bg-paper-2 shadow-2xl sm:w-[26rem]"
      >
        <header className="flex items-center gap-2 border-b border-rule-soft px-3 py-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <Icon name="croissant" className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-sm font-semibold text-ink">{NAME}</p>
            <p className="truncate text-[11px] text-ink-3">Your kitchen assistant</p>
          </div>
          <IconButton name="pencil" label="New conversation" onClick={() => void reset()} />
          <IconButton name="x" label="Close" onClick={onClose} />
        </header>

        <div ref={scroller} className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
          {messages.length === 0 && !sending && (
            <div className="pt-6 text-center">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-soft text-accent">
                <Icon name="croissant" className="h-7 w-7" />
              </span>
              <p className="mt-3 font-serif text-base text-ink">Bonjour.</p>
              <p className="mx-auto mt-1 max-w-[16rem] text-sm text-ink-3">
                Ask me what to cook, what's on the plan, or for a recipe built
                around what you actually have.
              </p>
            </div>
          )}

          {messages.map((m) => (
            <MessageView key={m.id} message={m} onSend={send} onMutated={onMutated} />
          ))}

          {sending && (
            <div className="flex items-center gap-2 text-sm text-ink-3">
              <Spinner className="h-3.5 w-3.5" />
              <span>{THINKING[thinkingIdx]}</span>
            </div>
          )}

          {starters && messages.length === 0 && (
            <div className="pt-2">
              <ChefBlockView block={starters} onSend={send} onMutated={onMutated} />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
              {error}
            </div>
          )}
        </div>

        <form
          className="border-t border-rule-soft bg-paper px-2.5 py-2.5"
          style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
          onSubmit={(e) => { e.preventDefault(); void send(draft); }}
        >
          <div className="flex items-end gap-2">
            <textarea
              ref={input}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                // Grow to fit, up to a point. A five-line box on a phone eats
                // the conversation it belongs to.
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onKeyDown={(e) => {
                // Enter sends, Shift-Enter breaks the line. The reverse is
                // right for a document and wrong for a chat.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
              placeholder="What should we have for dinner?"
              className="max-h-[120px] min-h-[2.5rem] flex-1 resize-none rounded-lg border border-rule-soft bg-paper-2 px-3 py-2 text-sm text-ink outline-none transition placeholder:text-ink-3 focus:border-accent/50"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              aria-label="Send"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent text-paper transition disabled:opacity-40"
            >
              <Icon name="send" className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1.5 px-1 text-[10px] leading-tight text-ink-3">
            Nothing changes until you tap to confirm. Allergy checks come from your
            diet profiles — always read the labels.
          </p>
        </form>
      </aside>
    </>
  );
}

function MessageView({ message, onSend, onMutated }: {
  message: ChefMessage;
  onSend: (t: string) => void;
  onMutated: () => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <p className={`max-w-[85%] rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-sm text-paper ${
          message.pending ? "opacity-60" : ""}`}
        >
          {message.content}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {message.blocks.map((b, i) => (
        <ChefBlockView key={i} block={b} onSend={onSend} onMutated={onMutated} />
      ))}
    </div>
  );
}

/**
 * The launcher.
 *
 * `variant="sidebar"` is a row in the desktop sidebar footer; `variant="fab"`
 * is the floating button on a phone. Both open the same panel, and the parent
 * owns the open state so only one panel ever exists.
 */
export function ChefLauncher({ variant, onOpen }: {
  variant: "sidebar" | "fab";
  onOpen: () => void;
}) {
  if (variant === "sidebar") {
    return (
      <button
        onClick={onOpen}
        className="flex w-full items-center gap-3 rounded-lg border border-accent/25 bg-accent-soft px-3 py-2.5 text-left text-sm text-accent transition hover:border-accent/50"
      >
        <Icon name="croissant" className="h-[18px] w-[18px] shrink-0" />
        <span className="flex-1 font-medium">Ask {NAME}</span>
        {/* The shortcut is advertised where the button is, because a keyboard
            shortcut nobody is told about is a shortcut nobody uses. */}
        <kbd className="rounded border border-accent/30 px-1 text-[10px] opacity-70">⌘K</kbd>
      </button>
    );
  }

  return (
    <button
      onClick={onOpen}
      aria-label={`Ask ${NAME}`}
      className="no-print fixed bottom-[4.75rem] right-4 z-30 grid h-14 w-14 place-items-center rounded-full bg-accent text-paper shadow-lg transition active:scale-95 md:hidden"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <Icon name="croissant" className="h-7 w-7" />
    </button>
  );
}
